// Date formatting helpers
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const formatTime = (timeString: string): string => {
  // Assumes time is in HH:mm format
  const [hours, minutes] = timeString.split(':').map(Number);
  const date = new Date();
  date.setHours(hours, minutes);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

export const formatDateRange = (date: string, startTime: string, endTime: string): string => {
  return `${formatDate(date)}, ${formatTime(startTime)} - ${formatTime(endTime)}`;
};

// Capacity helpers
export const getSpotsRemaining = (capacity: number, currentAttendees: number): number => {
  return Math.max(0, capacity - currentAttendees);
};

export const getCapacityPercentage = (capacity: number, currentAttendees: number): number => {
  if (capacity === 0) return 0;
  return Math.min(100, Math.round((currentAttendees / capacity) * 100));
};

export const isEventFull = (capacity: number, currentAttendees: number): boolean => {
  return currentAttendees >= capacity;
};

// Date comparison helpers
export const isDateInPast = (dateString: string): boolean => {
  const eventDate = new Date(dateString);
  const today = new Date();
  // Compare dates only (ignore time) - set both to start of day
  eventDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return eventDate < today;
};

export const isDateTodayOrFuture = (dateString: string): boolean => {
  return !isDateInPast(dateString);
};

// Check if event should still be visible (not ended more than 1 hour ago)
export const isEventStillVisible = (dateString: string, endTime: string): boolean => {
  // Extract just the date portion (handles both "2026-02-03" and "2026-02-03T00:00:00.000Z")
  const datePart = dateString.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = endTime.split(':').map(Number);

  // Create date object for event end time
  const eventEndDateTime = new Date(year, month - 1, day, hours, minutes);

  // Add 1 hour grace period
  const hideAfter = new Date(eventEndDateTime.getTime() + 60 * 60 * 1000);

  return new Date() < hideAfter;
};

// Registration deadline helpers
export const isRegistrationOpen = (deadline: string, deadlineTime?: string): boolean => {
  // Extract just the date portion (handles both "2026-02-03" and "2026-02-03T00:00:00.000Z")
  const datePart = deadline.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  const deadlineDate = new Date(year, month - 1, day); // month is 0-indexed
  if (deadlineTime) {
    const [hours, minutes] = deadlineTime.split(':').map(Number);
    deadlineDate.setHours(hours, minutes, 59, 999);
  } else {
    // Default to end of day (23:59:59) if no time specified
    deadlineDate.setHours(23, 59, 59, 999);
  }
  return deadlineDate > new Date();
};

export const getDaysUntilDeadline = (deadline: string): number => {
  const today = new Date();
  const deadlineDate = new Date(deadline);
  const diffTime = deadlineDate.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// String helpers
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

export const getInitials = (firstName: string, lastName: string): string => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

// Volunteer helpers
export const isVolunteerOnly = (user: { isVolunteer?: boolean; volunteerStatus?: string | null; familyMembers?: any[] } | null): boolean => {
  return !!(user?.isVolunteer && user?.volunteerStatus === 'approved' && (!user?.familyMembers || user.familyMembers.length === 0));
};

// ID generation (for mock data)
export const generateId = (): string => {
  return Math.random().toString(36).substring(2, 11);
};
